import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const JOBS = [
  {
    id: 1,
    title: "Frontend Developer (React)",
    role: "Engineering",
    type: "Hybrid",
    description:
      "Work on modern React applications, build scalable UI components, and collaborate with designers and backend engineers.",
  },
  {
    id: 2,
    title: "Backend Developer",
    role: "Engineering",
    type: "Remote",
    description:
      "Design and develop secure, scalable APIs and backend services.",
  },
  {
    id: 3,
    title: "UI/UX Designer",
    role: "Design",
    type: "Hybrid",
    description:
      "Create intuitive user experiences and maintain design consistency across products.",
  },
  {
    id: 4,
    title: "Digital Marketing Executive",
    role: "Marketing",
    type: "Remote",
    description:
      "Plan and execute digital marketing campaigns focused on growth and engagement.",
  },
];

export default function Careers() {
  const [search, setSearch] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const applyRef = useRef(null);

  const filteredJobs = JOBS.filter((job) =>
    job.title.toLowerCase().includes(search.toLowerCase())
  );

  const scrollToApply = () => {
    applyRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="pt-40 pb-24 max-w-7xl mx-auto px-6">

      {/* Header */}
      <h1 className="text-4xl md:text-5xl font-bold mb-6">
        Careers at <span className="text-blue-600">360Support</span>
      </h1>

      <p className="text-lg text-slate-600 dark:text-slate-400 max-w-3xl mb-10">
        We’re building a team of motivated people who enjoy solving real-world
        problems through technology and creativity.
      </p>

      {/* Search */}
      <input
        type="text"
        placeholder="Search roles (e.g. Frontend, Designer)"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full md:w-96 mb-12 px-4 py-3 rounded-xl
        border border-black/10 dark:border-white/10
        bg-white dark:bg-[#0B1022]"
      />

      {/* Job Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {filteredJobs.map((job) => (
          <motion.div
            key={job.id}
            whileHover={{ y: -6 }}
            className="p-6 rounded-2xl bg-white dark:bg-[#0B1022]
            border border-black/5 dark:border-white/10 shadow-md"
          >
            <h3 className="text-xl font-semibold mb-2">{job.title}</h3>

            <div className="flex gap-3 text-sm mb-4">
              <span className="px-3 py-1 rounded-full bg-blue-600/10 text-blue-600">
                {job.role}
              </span>
              <span className="px-3 py-1 rounded-full bg-slate-200 dark:bg-slate-700">
                {job.type}
              </span>
            </div>

            <div className="flex gap-4 mt-4">
              <button
                onClick={() => setSelectedJob(job)}
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                View Details
              </button>

              <button
                onClick={scrollToApply}
                className="text-sm font-medium text-slate-600 dark:text-slate-400 hover:underline"
              >
                Apply
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* HOW TO APPLY – HIGHLIGHTED */}
      <div
        ref={applyRef}
        className="mt-24 p-10 rounded-3xl
        bg-gradient-to-r from-blue-600 to-indigo-600
        text-white shadow-2xl"
      >
        <h2 className="text-3xl font-bold mb-4">How to Apply</h2>
        <p className="mb-4 text-white/90 max-w-2xl">
          If you’re interested in any of the above roles, send your updated resume
          and portfolio to our HR team. Please mention the role you’re applying for
          in the subject line.
        </p>
        <p className="text-xl font-semibold">
          📧 hr@360support.com
        </p>
      </div>

      {/* JOB DETAILS MODAL */}
      <AnimatePresence>
        {selectedJob && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50
            flex items-center justify-center px-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white dark:bg-[#0B1022]
              max-w-lg w-full p-8 rounded-2xl"
            >
              <h3 className="text-2xl font-semibold mb-2">
                {selectedJob.title}
              </h3>

              <p className="text-slate-600 dark:text-slate-400 mb-6">
                {selectedJob.description}
              </p>

              <button
                onClick={() => setSelectedJob(null)}
                className="px-5 py-2 rounded-lg bg-blue-600
                text-white hover:bg-blue-700 transition"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </section>
  );
}
